import { useCallback, useMemo } from 'react';
import { LogicalSpace, TrafficRule } from '@thive/fddm-types';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';


export function useLogicalSpaceTrafficRuleActions(
  logicalSpace: LogicalSpace,
) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const updateTrafficRules = useCallback(
    (updater: (rules: TrafficRule[]) => TrafficRule[]) => {
      const nextTrafficRules = updater(logicalSpace.trafficRules);

      if (nextTrafficRules === logicalSpace.trafficRules) return;

      const nextLogicalSpace: LogicalSpace = {
        ...logicalSpace,
        trafficRules: nextTrafficRules,
      };

      fddmSvc.updateLogicalSpace(nextLogicalSpace);
    },
    [fddmSvc, logicalSpace],
  );

  const setDoNotStopEnabled = useCallback(
    (enabled: boolean) => {
      updateTrafficRules((rules) => {
        const exists = rules.some((rule) => rule.type === 'DO_NOT_STOP');

        if (enabled) {
          if (exists) return rules;
          return [...rules, { type: 'DO_NOT_STOP' }];
        }

        if (!exists) return rules;
        return rules.filter((rule) => rule.type !== 'DO_NOT_STOP');
      });
    },
    [updateTrafficRules],
  );

  const setRestrictedAccessEnabled = useCallback(
    (enabled: boolean) => {
      updateTrafficRules((rules) => {
        const exists = rules.some((rule) => rule.type === 'RESTRICTED_ACCESS');

        if (enabled) {
          if (exists) return rules;
          return [...rules, { type: 'RESTRICTED_ACCESS' }];
        }

        if (!exists) return rules;
        return rules.filter((rule) => rule.type !== 'RESTRICTED_ACCESS');
      });
    },
    [updateTrafficRules],
  );

  const setMaxSpeed = useCallback(
    (value: number | '') => {
      updateTrafficRules((rules) => {
        const nextValue = value === '' ? undefined : value;
        const existingRuleIndex = rules.findIndex((rule) => rule.type === 'SPEED_LIMIT');

        if (nextValue === undefined) {
          if (existingRuleIndex === -1) return rules;
          return rules.filter((rule) => rule.type !== 'SPEED_LIMIT');
        }

        const nextRule: TrafficRule = {
          type: 'SPEED_LIMIT',
          speedLimitExtension: {
            maxSpeed: nextValue,
          },
        };

        if (existingRuleIndex === -1) {
          return [...rules, nextRule];
        }

        const currentRule = rules[existingRuleIndex];
        if (currentRule.speedLimitExtension?.maxSpeed === nextValue) return rules;

        const nextRules = [...rules];
        nextRules[existingRuleIndex] = nextRule;
        return nextRules;
      });
    },
    [updateTrafficRules],
  );

  const toggleVehicleMode = useCallback(
    (vehicleTypeId: string, mode: string) => {
      if (!vehicleTypeId || !mode) return;

      updateTrafficRules((rules) => {
        const existingRuleIndex = rules.findIndex(
          (rule) =>
            rule.type === 'VEHICLE_MODE' &&
            rule.vehicleModeExtension?.vehicleTypeId === vehicleTypeId,
        );

        if (existingRuleIndex === -1) {
          const nextRule: TrafficRule = {
            type: 'VEHICLE_MODE',
            vehicleModeExtension: {
              vehicleTypeId,
              vehicleModes: [mode],
            },
          };

          return [...rules, nextRule];
        }

        const currentRule = rules[existingRuleIndex];
        const currentExtension = currentRule.vehicleModeExtension;

        if (!currentExtension) return rules;

        const currentModes = currentExtension.vehicleModes;
        const hasMode = currentModes.includes(mode);

        if (hasMode) {
          const remainingModes = currentModes.filter((m) => m !== mode);

          const nextRules = [...rules];

          if (remainingModes.length === 0) {
            return nextRules.filter((_, index) => index !== existingRuleIndex);
          }

          nextRules[existingRuleIndex] = {
            type: 'VEHICLE_MODE',
            vehicleModeExtension: {
              vehicleTypeId,
              vehicleModes: remainingModes as [string, ...string[]],
            },
          };

          return nextRules;
        }

        const nextRules = [...rules];
        nextRules[existingRuleIndex] = {
          type: 'VEHICLE_MODE',
          vehicleModeExtension: {
            vehicleTypeId,
            vehicleModes: [...currentModes, mode] as [string, ...string[]],
          },
        };

        return nextRules;
      });
    },
    [updateTrafficRules],
  );

  return {
    setDoNotStopEnabled,
    setRestrictedAccessEnabled,
    setMaxSpeed,
    toggleVehicleMode,
  };
}
